import type { ChineseSeoLocale } from "./locale-rollout";
import type { Wave1HomeContent } from "./wave1-home";

const useCaseHrefs = {
  youtube: "/faceless-youtube-video-generator",
  tiktok: "/faceless-tiktok-video-generator",
  instagram: "/faceless-instagram-reels-generator",
  podcast: "/faceless-podcast-video-generator",
};

export const chineseHomeContent: Record<
  ChineseSeoLocale,
  Wave1HomeContent
> = {
  zh: {
    metadata: {
      title: "AI 不露脸视频生成器与自动发布 | AriaFlow",
      description:
        "从创意、脚本或公开链接创建可编辑的不露脸视频，包含原创画面、AI 配音、字幕、人工审阅，以及 YouTube、TikTok、Instagram 和 X 发布。",
      keywords: [
        "AI 不露脸视频生成器",
        "无脸视频生成器",
        "YouTube 不露脸视频",
        "不露脸视频自动化",
        "脚本转不露脸视频",
        "链接转不露脸视频",
      ],
    },
    hero: {
      eyebrow: "AI 不露脸视频工作流",
      title: "自动创建并发布不露脸视频",
      description:
        "从创意、脚本或公开链接开始。检查每个细节后按计划发布，工作流成熟后也可以开启自动运行。",
      primary: "免费创建第一个工作流",
      secondary: "查看真实样例",
      previewAlt:
        "AriaFlow 不露脸视频工作流，展示可编辑内容计划和生成状态",
      caption:
        "默认保留发布前审阅，也可以在工作流成熟后开启自动运行。",
    },
    workflowTypes: {
      title: "选择适合你的不露脸视频入口",
      subtitle:
        "从主题、已完成脚本或公开链接开始。AriaFlow 会先生成可审阅草稿，不会直接替你发布。",
      action: "查看工作流",
      items: [
        {
          title: "主题转不露脸视频",
          description:
            "输入一个明确主题，建立内容计划、首篇脚本和可重复使用的系列方向。",
          output: "适合新频道和持续更新的内容系列",
          href: "/faceless-video-automation",
        },
        {
          title: "脚本转不露脸视频",
          description:
            "保留已经确认的旁白内容，把脚本拆分成可编辑场景。",
          output: "适合作者、课程和已审核内容",
          href: "/script-to-faceless-video",
        },
        {
          title: "链接转不露脸视频",
          description:
            "把公开网页改写成适合口播和视觉呈现的视频草稿。",
          output: "适合文章、产品页和来源型讲解",
          href: "/url-to-faceless-video",
        },
      ],
    },
    samples: {
      title: "AriaFlow 制作的真实 9:16 竖屏成片",
      description:
        "从故事、英语对话到书籍解读和长内容，每个样例都来自真实的 AriaFlow 工作流。先比较叙事、字幕、节奏和画面方向，再开始自己的创作。",
      cta: "创建不露脸视频",
      publicExample: "查看公开样例",
      englishDisclosure: "当前样例使用英语配音或英语字幕",
      secondShort: "秒",
      minuteShort: "分钟",
      items: {
        history: {
          label: "历史故事",
          title: "替城市承受轰炸的假目标",
          description:
            "一条讲述二战英国诱饵地点的竖屏历史视频，包含连贯角色、旁白、字幕和分镜画面。",
          transcript:
            "脚本解释英国如何利用灯光模拟城镇，把德国轰炸机从布里斯托尔引向诱饵地点。",
        },
        scary: {
          label: "悬疑故事",
          title: "逐步升级的悬疑短片",
          description:
            "通过直接开场、逐步升级的线索、深色视觉方向和同步字幕构建竖屏叙事。",
          transcript:
            "脚本从一个无法解释的细节开始，逐场提高风险，并在揭晓后迅速结束。",
        },
        longForm: {
          label: "长视频与播客",
          title: "摆脱懒散的晨间习惯",
          description:
            "一条约 9 分钟的英语学习播客视频，包含两种 AI 声音、完整脚本、字幕和统一演播室画面。",
          transcript:
            "节目通过一段长对话，讲解适合日常英语学习者的晨间习惯词汇和实用表达。",
        },
        education: {
          label: "URL 转视频",
          title: "AriaFlow 官网 URL 转视频示例",
          description:
            "将 AriaFlow 公开首页转换成 79 秒横屏产品视频，包含来源整理、旁白、字幕和产品相关场景。",
          transcript:
            "脚本根据公开页面介绍从创意、脚本或 URL 创建可编辑视频，并发布到已连接的社交渠道。",
        },
        url: {
          label: "链接转视频",
          title: "EnsembleData 产品概览",
          description:
            "根据公开网站制作的 70 秒横屏产品概览，使用重新组织的脚本、旁白、字幕和品牌化场景。",
          transcript:
            "脚本把网页内容整理为产品说明，介绍支持的社交平台、API 接入方式和常见数据用途。",
        },
      },
    },
    process: {
      title: "发布前始终保留完整审阅步骤",
      description:
        "AriaFlow 将“生成成功”和“允许发布”分开，方便你在视频进入社交平台前检查事实、画面、声音和账号设置。",
      steps: [
        {
          title: "输入",
          description: "输入明确主题、已确认脚本或可公开访问的来源链接。",
        },
        {
          title: "生成",
          description: "准备旁白、场景、原创画面、字幕、音乐和发布信息。",
        },
        {
          title: "审阅",
          description: "检查完整脚本、配音、画面、节奏、字幕和目标账号。",
        },
        {
          title: "排期或发布",
          description: "下载已确认视频，或按计划发送到已连接的社交账号。",
        },
      ],
      controlsTitle: "草稿始终由你控制",
      controls: [
        "编辑生成的脚本、章节和场景计划",
        "选择声音、视觉风格、画幅和画面节奏",
        "制作竖屏视频或最长 10 分钟的横屏视频",
        "发布前检查标题、说明和目标社交账号",
      ],
      platformsEyebrow: "支持发布的社交平台",
      platformsTitle: "把已审阅的视频发布到受众所在的平台",
      platformsDescription:
        "连接受支持的账号，检查发布目标和视频信息，再排期或发布到 YouTube、TikTok、Instagram 或 X。",
      platformsSupportedLabel: "支持发布",
      platformsDisclaimer:
        "发布能力取决于账号授权状态及各平台当前的 API 与内容规则。",
      directoryTitle: "按输入方式和平台选择不露脸视频工作流",
      directoryDescription:
        "选择与你目标最接近的页面。每个工作流都包含真实样例、实际限制和相关指南。",
      directoryLabel: "不露脸视频工作流",
    },
    pricing: {
      title: "按你的制作节奏选择价格方案",
      subtitle:
        "先免费创建并体验第一条工作流，再按制作节奏选择方案。积分用于脚本、画面、配音和视频合成。",
      view: "查看实时价格和积分成本",
      credits: "积分",
      estimate: "预计成片时长",
      disclaimer:
        "规划估算按每分钟成片约 500–750 积分计算。模型、声音、场景数量、重试次数和视频长度都会影响实际消耗。",
      perYear: "每年",
      perMonth: "每月",
      minutes: "分钟",
      planDescriptions: {
        basic: "适合验证首个工作流并制作一组稳定的视频草稿。",
        standard: "适合按固定计划持续制作多条视频的创作者。",
        pro: "适合需要更高产量和多个并行工作流的团队。",
      },
    },
    faq: {
      title: "不露脸视频常见问题",
      description:
        "开始第一条视频前，先了解输入方式、视频长度、社交发布和内容权利。",
      items: [
        {
          question: "什么是 AI 不露脸视频生成器？",
          answer:
            "它是一套无需真人出镜的视频制作工作流。AriaFlow 会组合脚本、AI 配音、生成或选择的画面、字幕、音乐和审阅控制。",
        },
        {
          question: "可以使用我自己的脚本吗？",
          answer:
            "可以。粘贴完整脚本，检查预计时长和场景计划，再选择声音、视觉风格、字幕、画幅和发布方式。",
        },
        {
          question: "AriaFlow 可以把链接转成视频吗？",
          answer:
            "可以处理受支持的公开网页并生成可编辑的口播改写。付费墙、私有页面或阻止抓取的网站可能需要你改为粘贴脚本。",
        },
        {
          question: "不露脸视频最长可以做多长？",
          answer:
            "当前工作流支持最长 10 分钟。积分和生成时间取决于时长、视觉模型、配音和合成设置。",
        },
        {
          question: "AriaFlow 可以自动发布到社交平台吗？",
          answer:
            "可以连接受支持的 YouTube、TikTok、Instagram 和 X 账号。请在排期或发布前检查视频和目标账号。",
        },
        {
          question: "生成的视频归谁所有？",
          answer:
            "最终视频文件归你所有，但仍需遵守来源素材的权利，以及制作中使用的第三方模型、媒体、音乐和声音条款。",
        },
      ],
    },
    cta: {
      title: "创建第一条可完整审阅的不露脸视频",
      description:
        "从创意、脚本或链接开始，在下载、排期或发布前检查完整草稿。",
      button: "创建不露脸视频",
    },
    navigation: {
      facelessVideo: "不露脸视频",
      useCases: "使用场景",
      resources: "资源",
      pricing: "价格",
      dashboard: "控制台",
      signIn: "登录",
      startFree: "免费开始",
      menu: "菜单",
      close: "关闭",
      useCaseLinks: [
        {
          href: useCaseHrefs.youtube,
          label: "YouTube 不露脸视频",
          description: "制作 Shorts 和最长 10 分钟的频道视频。",
        },
        {
          href: useCaseHrefs.tiktok,
          label: "TikTok 不露脸视频",
          description: "制作带有清晰字幕的竖屏短视频。",
        },
        {
          href: useCaseHrefs.instagram,
          label: "Instagram Reels",
          description: "制作风格统一、无需真人出镜的 Reels。",
        },
        {
          href: useCaseHrefs.podcast,
          label: "播客视频",
          description: "把双人对话脚本转成可视化节目。",
        },
      ],
    },
    footer: {
      description:
        "从创意、脚本或链接创建可编辑的不露脸视频，在下载、排期或发布前完成审阅。",
      productTitle: "不露脸视频",
      useCasesTitle: "使用场景",
      resourcesTitle: "资源",
      terms: "服务条款",
      privacy: "隐私政策",
      english: "英语",
      productLinks: [
        ["/script-to-faceless-video", "脚本转视频"],
        ["/url-to-faceless-video", "链接转视频"],
        ["/long-form-faceless-video-generator", "长视频生成"],
        ["/faceless-video-automation", "视频自动化"],
      ],
      useCaseLinks: [
        [useCaseHrefs.youtube, "YouTube 视频"],
        [useCaseHrefs.tiktok, "TikTok 视频"],
        [useCaseHrefs.instagram, "Instagram Reels"],
        [useCaseHrefs.podcast, "播客视频"],
      ],
      resourceLinks: [
        ["/blog", "不露脸视频博客（英语）"],
        ["/examples/history-faceless-video", "真实视频样例（英语）"],
        [
          "/resources/faceless-video-script-templates",
          "免费脚本模板（英语）",
        ],
        [
          "/tools/faceless-video-length-calculator",
          "视频时长计算器（英语）",
        ],
        ["/pricing", "价格"],
      ],
    },
    marketing: {
      create: "创建不露脸视频",
      watchSample: "观看样例",
      englishSample: "当前样例使用英语配音或英语字幕。",
      whoFits: "适合哪些创作者和团队",
      workflowTitle: "从输入到已确认视频",
      limitsTitle: "开始前需要了解的限制",
      relatedTitle: "相关指南和工作流",
      faqTitle: "常见问题",
      finalTitle: "创建一条可以完整审阅的不露脸视频",
      finalDescription:
        "从创意、脚本或链接开始，在下载、排期或发布前完成检查。",
      englishLabel: "英语",
      viewPublicExample: "查看公开样例",
    },
  },
  "zh-tw": {
    metadata: {
      title: "AI 不露臉影片產生器與自動發布 | AriaFlow",
      description:
        "從點子、腳本或公開網址建立可編輯的不露臉影片，包含原創畫面、AI 語音、字幕、人工檢查，以及 YouTube、TikTok、Instagram 和 X 發布。",
      keywords: [
        "AI 不露臉影片產生器",
        "AI 無露臉影片產生器",
        "無臉影片產生器",
        "YouTube 不露臉影片",
        "不露臉影片自動化",
        "腳本轉不露臉影片",
      ],
    },
    hero: {
      eyebrow: "AI 不露臉影片產生器",
      title: "自動建立並發佈不露臉影片",
      description:
        "從點子、腳本或公開網址開始。檢查每個細節後按計畫發佈，工作流程準備好後也可以開啟自動運行。",
      primary: "免費建立第一個工作流程",
      secondary: "查看真實範例",
      previewAlt:
        "AriaFlow 不露臉影片工作流程，顯示可編輯的內容計畫與產生狀態",
      caption:
        "發佈前先檢查，或在工作流程準備好後開啟自動運行。",
    },
    workflowTypes: {
      title: "選擇適合你的不露臉影片起點",
      subtitle:
        "從主題、完成的腳本或公開網址開始。AriaFlow 會先建立可檢查的草稿，不會直接替你發布。",
      action: "查看工作流程",
      items: [
        {
          title: "主題轉不露臉影片",
          description:
            "輸入明確主題，建立內容計畫、第一份腳本與可重複使用的系列方向。",
          output: "適合新頻道與固定更新的內容系列",
          href: "/faceless-video-automation",
        },
        {
          title: "腳本轉不露臉影片",
          description:
            "保留已確認的旁白內容，將腳本分成可編輯的影片場景。",
          output: "適合作家、課程與已審核內容",
          href: "/script-to-faceless-video",
        },
        {
          title: "網址轉不露臉影片",
          description:
            "把公開網頁改寫成適合口述和視覺呈現的影片草稿。",
          output: "適合文章、產品頁與來源型解說",
          href: "/url-to-faceless-video",
        },
      ],
    },
    samples: {
      title: "AriaFlow 製作的真實 9:16 直式成片",
      description:
        "從故事、英語對話到書籍解讀與長內容，每個範例都來自真實的 AriaFlow 工作流程。先比較敘事、字幕、節奏與畫面方向，再開始自己的創作。",
      cta: "建立不露臉影片",
      publicExample: "查看公開範例",
      englishDisclosure: "目前範例使用英文語音或英文字幕",
      secondShort: "秒",
      minuteShort: "分鐘",
      items: {
        history: {
          label: "歷史故事",
          title: "替城市承受轟炸的假目標",
          description:
            "介紹二戰英國誘餌地點的直式歷史影片，包含連貫角色、旁白、字幕與分鏡畫面。",
          transcript:
            "腳本說明英國如何用燈光模擬城鎮，把德國轟炸機從布里斯托引向誘餌地點。",
        },
        scary: {
          label: "懸疑故事",
          title: "逐步升高張力的懸疑短片",
          description:
            "以直接開場、逐步出現的線索、深色視覺方向和同步字幕建立直式敘事。",
          transcript:
            "腳本從一個無法解釋的細節開始，逐場提高風險，並在揭曉後快速結束。",
        },
        longForm: {
          label: "長影片與 Podcast",
          title: "擺脫懶散的晨間習慣",
          description:
            "約 9 分鐘的英語學習 Podcast 影片，包含兩種 AI 聲音、完整腳本、字幕與一致的錄音室畫面。",
          transcript:
            "節目透過一段長對話，介紹適合日常英語學習者的晨間習慣單字與實用表達。",
        },
        education: {
          label: "URL 轉影片",
          title: "AriaFlow 官網 URL 轉影片範例",
          description:
            "將 AriaFlow 公開首頁轉換成 79 秒橫式產品影片，包含來源整理、旁白、字幕與產品相關場景。",
          transcript:
            "腳本依公開頁面介紹如何從創意、腳本或 URL 建立可編輯影片，並發佈到已連接的社群管道。",
        },
        url: {
          label: "網址轉影片",
          title: "EnsembleData 產品概覽",
          description:
            "依公開網站製作的 70 秒橫式產品概覽，使用重新整理的腳本、旁白、字幕與品牌化場景。",
          transcript:
            "腳本將網頁內容整理成產品說明，介紹支援的社群平台、API 存取方式與常見資料用途。",
        },
      },
    },
    process: {
      title: "發布前保留完整的人工檢查步驟",
      description:
        "AriaFlow 將「產生成功」與「允許發布」分開，讓你在影片進入社群平台前檢查事實、畫面、語音與帳號設定。",
      steps: [
        {
          title: "輸入",
          description: "輸入明確主題、已確認腳本或可公開存取的來源網址。",
        },
        {
          title: "產生",
          description: "準備旁白、場景、原創畫面、字幕、音樂與發布資訊。",
        },
        {
          title: "檢查",
          description: "確認完整腳本、語音、畫面、節奏、字幕與目標帳號。",
        },
        {
          title: "排程或發布",
          description: "下載已確認的影片，或依排程傳送至已連結的社群帳號。",
        },
      ],
      controlsTitle: "草稿始終由你控制",
      controls: [
        "編輯產生的腳本、章節與場景計畫",
        "選擇語音、視覺風格、長寬比與畫面節奏",
        "製作直式影片或最長 10 分鐘的橫式影片",
        "發布前檢查標題、說明與目標社群帳號",
      ],
      platformsEyebrow: "支援發布的社群平台",
      platformsTitle: "將已確認的影片發布到觀眾所在的平台",
      platformsDescription:
        "連結支援的帳號，確認發布目標與影片資訊，再排程或發布至 YouTube、TikTok、Instagram 或 X。",
      platformsSupportedLabel: "支援發布",
      platformsDisclaimer:
        "發布功能取決於帳號授權狀態，以及各平台目前的 API 與內容規則。",
      directoryTitle: "依輸入方式與平台選擇不露臉影片工作流程",
      directoryDescription:
        "選擇最接近目標的頁面。每個工作流程都包含真實範例、實際限制與相關指南。",
      directoryLabel: "不露臉影片工作流程",
    },
    pricing: {
      title: "依你的製作節奏選擇價格方案",
      subtitle:
        "點數用於腳本、畫面、語音與影片合成。選擇方案前，先查看可製作分鐘數的估算範圍。",
      view: "查看即時價格與點數成本",
      credits: "點數",
      estimate: "預估成片長度",
      disclaimer:
        "規劃估算以每分鐘成片約 500–750 點計算。模型、語音、場景數量、重試次數與影片長度都會影響實際用量。",
      perYear: "每年",
      perMonth: "每月",
      minutes: "分鐘",
      planDescriptions: {
        basic: "適合驗證第一個工作流程並製作一組穩定的影片草稿。",
        standard: "適合依固定排程持續製作多支影片的創作者。",
        pro: "適合需要更高產量與多個平行工作流程的團隊。",
      },
    },
    faq: {
      title: "不露臉影片常見問題",
      description:
        "開始第一支影片前，先了解輸入方式、影片長度、社群發布與內容權利。",
      items: [
        {
          question: "什麼是 AI 不露臉影片產生器？",
          answer:
            "它是一套不需要本人上鏡的影片製作工作流程。AriaFlow 會組合腳本、AI 語音、產生或選擇的畫面、字幕、音樂與檢查控制。",
        },
        {
          question: "可以使用自己的腳本嗎？",
          answer:
            "可以。貼上完整腳本，確認預估長度與場景計畫，再選擇語音、視覺風格、字幕、長寬比與發布方式。",
        },
        {
          question: "AriaFlow 可以把網址轉成影片嗎？",
          answer:
            "可以處理支援的公開網頁並建立可編輯的口述改寫。付費牆、私人頁面或封鎖擷取的網站可能需要你改為貼上腳本。",
        },
        {
          question: "不露臉影片最長可以製作多久？",
          answer:
            "目前工作流程支援最長 10 分鐘。點數與產生時間取決於長度、視覺模型、語音和合成設定。",
        },
        {
          question: "AriaFlow 可以自動發布到社群平台嗎？",
          answer:
            "可以連結支援的 YouTube、TikTok、Instagram 與 X 帳號。請在排程或發布前確認影片與目標帳號。",
        },
        {
          question: "產生的影片歸誰所有？",
          answer:
            "最終影片檔案歸你所有，但仍需遵守來源素材權利，以及製作時使用的第三方模型、媒體、音樂和聲音條款。",
        },
      ],
    },
    cta: {
      title: "建立第一支可完整檢查的不露臉影片",
      description:
        "從點子、腳本或網址開始，在下載、排程或發布前確認完整草稿。",
      button: "建立不露臉影片",
    },
    navigation: {
      facelessVideo: "不露臉影片",
      useCases: "使用情境",
      resources: "資源",
      pricing: "價格",
      dashboard: "控制台",
      signIn: "登入",
      startFree: "免費開始",
      menu: "選單",
      close: "關閉",
      useCaseLinks: [
        {
          href: useCaseHrefs.youtube,
          label: "YouTube 不露臉影片",
          description: "製作 Shorts 與最長 10 分鐘的頻道影片。",
        },
        {
          href: useCaseHrefs.tiktok,
          label: "TikTok 不露臉影片",
          description: "製作具有清楚字幕的直式短影片。",
        },
        {
          href: useCaseHrefs.instagram,
          label: "Instagram Reels",
          description: "製作風格一致、不需本人出鏡的 Reels。",
        },
        {
          href: useCaseHrefs.podcast,
          label: "Podcast 影片",
          description: "將雙人對話腳本轉成視覺化節目。",
        },
      ],
    },
    footer: {
      description:
        "從點子、腳本或網址建立可編輯的不露臉影片，在下載、排程或發布前完成檢查。",
      productTitle: "不露臉影片",
      useCasesTitle: "使用情境",
      resourcesTitle: "資源",
      terms: "服務條款",
      privacy: "隱私權政策",
      english: "英文",
      productLinks: [
        ["/script-to-faceless-video", "腳本轉影片"],
        ["/url-to-faceless-video", "網址轉影片"],
        ["/long-form-faceless-video-generator", "長影片產生"],
        ["/faceless-video-automation", "影片自動化"],
      ],
      useCaseLinks: [
        [useCaseHrefs.youtube, "YouTube 影片"],
        [useCaseHrefs.tiktok, "TikTok 影片"],
        [useCaseHrefs.instagram, "Instagram Reels"],
        [useCaseHrefs.podcast, "Podcast 影片"],
      ],
      resourceLinks: [
        ["/blog", "不露臉影片部落格（英文）"],
        ["/examples/history-faceless-video", "真實影片範例（英文）"],
        [
          "/resources/faceless-video-script-templates",
          "免費腳本範本（英文）",
        ],
        [
          "/tools/faceless-video-length-calculator",
          "影片長度計算器（英文）",
        ],
        ["/pricing", "價格"],
      ],
    },
    marketing: {
      create: "建立不露臉影片",
      watchSample: "觀看範例",
      englishSample: "目前範例使用英文語音或英文字幕。",
      whoFits: "適合哪些創作者與團隊",
      workflowTitle: "從輸入到已確認影片",
      limitsTitle: "開始前需要了解的限制",
      relatedTitle: "相關指南與工作流程",
      faqTitle: "常見問題",
      finalTitle: "建立一支可完整檢查的不露臉影片",
      finalDescription:
        "從點子、腳本或網址開始，在下載、排程或發布前完成確認。",
      englishLabel: "英文",
      viewPublicExample: "查看公開範例",
    },
  },
};
